import { CalendarMonth } from "@mui/icons-material";
import {
    Box,
    IconButton,
    LinearProgress,
    Paper,
    TableBody,
    TableCell,
    TableHead,
    Tooltip,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useCallback, useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable, {
    StyledHeaderTableRow,
    StyledPrimaryTableCell,
    StyledSecondaryTableCell,
    StyledTableRow,
    StyledTableSortLabel,
} from "@/components/Table/DataTable.tsx";
import SearchSection from "@/components/Table/SearchSection.tsx";
import { useQueryOptionsFactory } from "@/queries";
import type { ListUnit } from "@/queries/units.ts";

type SortField =
    | "ownerName"
    | "rentalId"
    | "codedUnit"
    | "unitPhone"
    | "ownerEmail"
    | "ownerPhones"
    | "rentalAgentPhone";
type SortDirection = "asc" | "desc";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const unitsData: { data: ListUnit[] } = useSuspenseQuery(qof.units.list()).data;

    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [sortField, setSortField] = useState<SortField>("ownerName");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    // @todo infinite scroll pagination
    const [, setPage] = useState(0);
    const [displayedData, setDisplayedData] = useState<ListUnit[]>([]);
    const navigate = useNavigate();

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
        setPage(0);
        setDisplayedData([]);
        setHasMore(true);
    };

    const getSortedAndFilteredData = useCallback(() => {
        const filteredData = unitsData.data.filter((unit) => {
            const searchLower = searchTerm.toLowerCase();
            return (
                unit.owner.ownerName.toLowerCase().includes(searchLower) ||
                unit.owner.ownerFirstName.toLowerCase().includes(searchLower) ||
                unit.rentalId.toLowerCase().includes(searchLower) ||
                unit.codedUnit?.toLowerCase().includes(searchLower) ||
                unit.unitPhone?.toLowerCase().includes(searchLower) ||
                unit.owner.email.toLowerCase().includes(searchLower) ||
                unit.owner.phone?.toLowerCase().includes(searchLower) ||
                unit.owner.phone2?.toLowerCase().includes(searchLower) ||
                unit.rentalAgentPhone?.toLowerCase().includes(searchLower)
            );
        });

        return filteredData.sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;
            switch (sortField) {
                case "ownerName":
                    aValue = `${a?.owner?.ownerName} ${a?.owner?.ownerFirstName}`;
                    bValue = `${b?.owner?.ownerName} ${b?.owner?.ownerFirstName}`;
                    break;
                case "rentalId":
                    aValue = Number.parseInt(a.rentalId);
                    bValue = Number.parseInt(b.rentalId);
                    break;
                case "codedUnit":
                    aValue = a.codedUnit ?? "";
                    bValue = b.codedUnit ?? "";
                    break;
                case "unitPhone":
                    aValue = a.unitPhone ?? "";
                    bValue = b.unitPhone ?? "";
                    break;
                case "ownerEmail":
                    aValue = a.owner?.email;
                    bValue = b.owner?.email;
                    break;
                case "ownerPhones":
                    aValue = a.owner?.phone ?? "";
                    bValue = b.owner?.phone ?? "";
                    break;
                default: //rentalAgentPhone
                    aValue = a.rentalAgentPhone ?? "";
                    bValue = b.rentalAgentPhone ?? "";
                    break;
            }

            if (typeof aValue === "string" && typeof bValue === "string") {
                return (sortDirection === "asc" ? -1 : 1) * aValue.localeCompare(bValue);
            }

            if (aValue < bValue) {
                return sortDirection === "asc" ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === "asc" ? 1 : -1;
            }
            return 0;
        });
    }, [searchTerm, sortField, sortDirection, unitsData]);

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title="Unit Information - Detailed View"
                    instructions={[
                        "View detailed information for all units and owners",
                        "Sort by clicking on column headings",
                        "Search by owner name, unit number, email, or phone",
                        "Click calendar icon to view unit reservations",
                    ]}
                />

                <SearchSection
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onClearSearch={() => setSearchTerm("")}
                    placeholder="Search by owner, unit number, coded unit, email, phone, or agent..."
                    displayedCount={displayedData.length}
                    totalCount={unitsData.data.length}
                    totalAllCount={unitsData.data.length}
                />

                <DataTable
                    isLoading={isLoading}
                    hasMore={hasMore}
                    onLoadMore={() => {
                        return;
                    }}
                    isEmpty={displayedData.length === 0}
                    emptyMessage={
                        searchTerm
                            ? "No units found matching your search."
                            : "No unit information available."
                    }
                    colSpan={8}
                    maxHeight={{ xs: "600px", sm: "600px" }}
                    loadingMessage="Loading more unit information..."
                    endMessage="No more units to load"
                >
                    <TableHead>
                        <StyledHeaderTableRow>
                            <TableCell sx={{ minWidth: { xs: "120px", sm: "160px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "ownerName"}
                                    direction={sortField === "ownerName" ? sortDirection : "asc"}
                                    onClick={() => handleSort("ownerName")}
                                >
                                    Owner Name
                                </StyledTableSortLabel>
                            </TableCell>
                            <TableCell sx={{ minWidth: { xs: "60px", sm: "80px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "rentalId"}
                                    direction={sortField === "rentalId" ? sortDirection : "asc"}
                                    onClick={() => handleSort("rentalId")}
                                >
                                    Unit #
                                </StyledTableSortLabel>
                            </TableCell>
                            <TableCell sx={{ minWidth: { xs: "80px", sm: "100px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "codedUnit"}
                                    direction={sortField === "codedUnit" ? sortDirection : "asc"}
                                    onClick={() => handleSort("codedUnit")}
                                >
                                    Coded Unit #
                                </StyledTableSortLabel>
                            </TableCell>
                            <TableCell sx={{ minWidth: { xs: "100px", sm: "120px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "unitPhone"}
                                    direction={sortField === "unitPhone" ? sortDirection : "asc"}
                                    onClick={() => handleSort("unitPhone")}
                                >
                                    Unit Phone
                                </StyledTableSortLabel>
                            </TableCell>
                            <TableCell sx={{ minWidth: { xs: "140px", sm: "160px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "ownerEmail"}
                                    direction={sortField === "ownerEmail" ? sortDirection : "asc"}
                                    onClick={() => handleSort("ownerEmail")}
                                >
                                    Owner Email
                                </StyledTableSortLabel>
                            </TableCell>
                            <TableCell sx={{ minWidth: { xs: "120px", sm: "140px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "ownerPhones"}
                                    direction={sortField === "ownerPhones" ? sortDirection : "asc"}
                                    onClick={() => handleSort("ownerPhones")}
                                >
                                    Owner Phone(s)
                                </StyledTableSortLabel>
                            </TableCell>
                            <TableCell sx={{ minWidth: { xs: "120px", sm: "140px" } }}>
                                <StyledTableSortLabel
                                    active={sortField === "rentalAgentPhone"}
                                    direction={
                                        sortField === "rentalAgentPhone" ? sortDirection : "asc"
                                    }
                                    onClick={() => handleSort("rentalAgentPhone")}
                                >
                                    On-Island Agent
                                </StyledTableSortLabel>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ minWidth: { xs: "80px", sm: "100px" } }}
                            >
                                View Calendar
                            </TableCell>
                        </StyledHeaderTableRow>
                    </TableHead>
                    <TableBody>
                        {getSortedAndFilteredData().map((unitsData, index) => (
                            <StyledTableRow
                                key={unitsData.id}
                                sx={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc" }}
                            >
                                <StyledPrimaryTableCell sx={{ wordBreak: "break-word" }}>
                                    {unitsData.owner?.ownerName}, {unitsData.owner?.ownerFirstName}
                                </StyledPrimaryTableCell>
                                <StyledPrimaryTableCell sx={{ fontWeight: 600 }}>
                                    {unitsData.rentalId}
                                </StyledPrimaryTableCell>
                                <StyledPrimaryTableCell sx={{ fontSize: "0.8rem" }}>
                                    {unitsData.codedUnit}
                                </StyledPrimaryTableCell>
                                <StyledSecondaryTableCell>
                                    {unitsData.unitPhone || "-"}
                                </StyledSecondaryTableCell>
                                <StyledSecondaryTableCell>
                                    {unitsData.owner.email}
                                </StyledSecondaryTableCell>
                                <StyledSecondaryTableCell>
                                    {unitsData.owner.phone}
                                    <br />
                                    {unitsData.owner.phone2}
                                </StyledSecondaryTableCell>
                                <StyledSecondaryTableCell>
                                    {unitsData.rentalAgentPhone || "-"}
                                </StyledSecondaryTableCell>
                                <TableCell align="center">
                                    <Tooltip title={`View calendar for Unit ${unitsData.rentalId}`}>
                                        <IconButton
                                            onClick={() =>
                                                navigate({
                                                    to: `/admin/units/${unitsData.id}/calendar`,
                                                })
                                            }
                                            sx={{
                                                color: "#28638c",
                                                backgroundColor: "#e0f2fe",
                                                size: "small",
                                                "&:hover": {
                                                    backgroundColor: "#b3e5fc",
                                                    transform: "scale(1.05)",
                                                },
                                                transition: "all 0.2s ease",
                                                p: { xs: 0.5, sm: 0.75 },
                                            }}
                                        >
                                            <CalendarMonth
                                                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </DataTable>
            </Paper>
        </Box>
    );
};

export const Route = createFileRoute("/admin/units/information")({
    component: Root,
    pendingComponent: LinearProgress,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(context.qof.units.list());
    },
});
